export type DataMode = "loading" | "live" | "demo" | "offline";

export type OverviewKpis = {
  spendMinor: number;
  conversations: number;
  qualifiedLeads: number;
  quotes: number;
  bookings: number;
  revenueMinor: number;
  roas: number | null;
  currency: string;
};

export type InboxItem = {
  id: string;
  customerName: string;
  channel: string;
  latestMessage: string;
  lastActivityAt: string;
  leadStatus: string;
  aiMode: string;
  tripInterest?: string;
  travellerCount?: number;
  leadScore?: number;
};

export type MetaIntegration = {
  configured: boolean;
  graphVersion: string;
  accounts: Array<{
    externalAccountId: string;
    displayName: string;
    status: string;
    connectedAt: string;
    lastWebhookAt: string | null;
  }>;
};

export type ContentItem = {
  id: string; caption: string; mediaType: string; mediaProductType: string; permalink: string;
  mediaUrl: string; thumbnailUrl: string; publishedAt: string; likeCount: number; commentsCount: number; engagementScore: number;
};

export type CampaignItem = {
  id: string; name: string; status: string; currency: string; spendMinor: number;
  impressions: number; reach: number; clicks: number; ctr: number;
};

export type SyncStatus = {
  runs: Array<{ source: string; status: string; startedAt: string; completedAt: string; recordsSynced: number; errorMessage: string }>;
  adsConfigured: boolean;
};

export type ConversationDetail = {
  conversation: Record<string, unknown>;
  messages: Array<{ id: string; direction: string; senderType: string; body: string; deliveryStatus: string; occurredAt: string }>;
  journey: Array<{ eventType: string; occurredAt: string; data: string }>;
};

const apiBase = String(import.meta.env.VITE_GROWTH_API_BASE_URL || "").replace(/\/$/, "");

export const growthApiLoginUrl = `${apiBase}/api/admin/v1/integrations/meta`;

function requestHeaders(headers?: HeadersInit) {
  const result = new Headers(headers);
  result.set("Accept", "application/json");
  if (["127.0.0.1", "localhost"].includes(window.location.hostname)) {
    result.set("X-Demo-User", "local@yowheretravel.com");
  }
  return result;
}

async function readJson<T>(path: string, init: RequestInit = {}) {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: "include",
    ...init,
    headers: requestHeaders(init.headers),
  });
  if (!response.ok) throw new Error(`Growth API returned ${response.status}`);
  return response.json() as Promise<T>;
}

function numberValue(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export async function getOverview(signal?: AbortSignal): Promise<{ mode: "live" | "demo"; kpis: OverviewKpis }> {
  const payload = await readJson<Record<string, unknown>>("/api/admin/v1/overview", { signal });
  const raw = (payload.kpis || {}) as Record<string, unknown>;
  return {
    mode: payload.mode === "demo" ? "demo" : "live",
    kpis: {
      spendMinor: numberValue(raw.spendMinor ?? raw.spend_minor),
      conversations: numberValue(raw.conversations),
      qualifiedLeads: numberValue(raw.qualifiedLeads ?? raw.qualified_leads),
      quotes: numberValue(raw.quotes),
      bookings: numberValue(raw.bookings),
      revenueMinor: numberValue(raw.revenueMinor ?? raw.revenue_minor),
      roas: raw.roas == null ? null : numberValue(raw.roas),
      currency: String(raw.currency || "USD"),
    },
  };
}

export async function getInbox(signal?: AbortSignal): Promise<{ mode: "live" | "demo"; items: InboxItem[] }> {
  const payload = await readJson<{ mode?: string; items?: Array<Record<string, unknown>> }>("/api/admin/v1/inbox", { signal });
  return {
    mode: payload.mode === "demo" ? "demo" : "live",
    items: (payload.items || []).map((item) => ({
      id: String(item.id),
      customerName: String(item.customerName ?? item.customer_name ?? "Unknown customer"),
      channel: String(item.channel || "WEBSITE"),
      latestMessage: String(item.latestMessage ?? item.last_message_preview ?? ""),
      lastActivityAt: String(item.lastActivityAt ?? item.last_activity_at ?? ""),
      leadStatus: String(item.leadStatus ?? item.lead_status ?? "NEW"),
      aiMode: String(item.aiMode ?? item.ai_mode ?? "AI_ASSIST"),
      tripInterest: item.tripInterest || item.trip_interest ? String(item.tripInterest ?? item.trip_interest) : undefined,
      travellerCount: item.travellerCount == null && item.traveller_count == null ? undefined : numberValue(item.travellerCount ?? item.traveller_count),
      leadScore: item.leadScore == null && item.lead_score == null ? undefined : numberValue(item.leadScore ?? item.lead_score),
    })),
  };
}

export async function getMetaIntegration(signal?: AbortSignal): Promise<MetaIntegration> {
  const payload = await readJson<{
    configured?: boolean;
    graphVersion?: string;
    accounts?: Array<Record<string, unknown>>;
  }>("/api/admin/v1/integrations/meta", { signal });
  return {
    configured: Boolean(payload.configured),
    graphVersion: String(payload.graphVersion || ""),
    accounts: (payload.accounts || []).map((account) => ({
      externalAccountId: String(account.external_account_id || ""),
      displayName: String(account.display_name || "Instagram"),
      status: String(account.status || "UNKNOWN"),
      connectedAt: String(account.connected_at || ""),
      lastWebhookAt: account.last_webhook_at ? String(account.last_webhook_at) : null,
    })),
  };
}

export function saveRecommendationDecision(decision: "APPROVED" | "REJECTED") {
  return readJson("/api/admin/v1/recommendations/00000000-0000-4000-8000-000000000001/decision", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision }),
  });
}

export async function getContent(signal?: AbortSignal) {
  const payload = await readJson<{ items?: Array<Record<string, unknown>>; recommendations?: string[] }>("/api/admin/v1/content", { signal });
  return {
    items: (payload.items || []).map((item): ContentItem => ({
      id: String(item.id), caption: String(item.caption || "Untitled post"), mediaType: String(item.media_type || ""),
      mediaProductType: String(item.media_product_type || ""), permalink: String(item.permalink || ""),
      mediaUrl: String(item.media_url || ""), thumbnailUrl: String(item.thumbnail_url || ""), publishedAt: String(item.published_at || ""),
      likeCount: numberValue(item.like_count), commentsCount: numberValue(item.comments_count), engagementScore: numberValue(item.engagement_score),
    })),
    recommendations: payload.recommendations || [],
  };
}

export async function getCampaigns(signal?: AbortSignal) {
  const payload = await readJson<{ items?: Array<Record<string, unknown>> }>("/api/admin/v1/campaigns", { signal });
  return (payload.items || []).map((item): CampaignItem => ({
    id: String(item.campaign_external_id), name: String(item.campaign_name || "Campaign"), status: String(item.status || "UNKNOWN"),
    currency: String(item.currency || "USD"), spendMinor: numberValue(item.spend_minor), impressions: numberValue(item.impressions),
    reach: numberValue(item.reach), clicks: numberValue(item.clicks), ctr: numberValue(item.ctr),
  }));
}

export async function getSyncStatus(signal?: AbortSignal): Promise<SyncStatus> {
  const payload = await readJson<{ runs?: Array<Record<string, unknown>>; adsConfigured?: boolean }>("/api/admin/v1/sync/status", { signal });
  return {
    adsConfigured: Boolean(payload.adsConfigured),
    runs: (payload.runs || []).map((run) => ({ source: String(run.source), status: String(run.status),
      startedAt: String(run.started_at || ""), completedAt: String(run.completed_at || ""), recordsSynced: numberValue(run.records_synced),
      errorMessage: String(run.error_message || "") })),
  };
}

export function runMetaSync() {
  return readJson<{ results: Array<Record<string, unknown>>; completedAt: string }>("/api/admin/v1/sync/meta", { method: "POST" });
}

export async function getConversation(id: string, signal?: AbortSignal): Promise<ConversationDetail> {
  const payload = await readJson<{ conversation: Record<string, unknown>; messages?: Array<Record<string, unknown>>; journey?: Array<Record<string, unknown>> }>(`/api/admin/v1/conversations/${encodeURIComponent(id)}`, { signal });
  return {
    conversation: payload.conversation,
    messages: (payload.messages || []).map((message) => ({ id: String(message.id), direction: String(message.direction),
      senderType: String(message.sender_type), body: String(message.body || ""), deliveryStatus: String(message.delivery_status || ""),
      occurredAt: String(message.occurred_at || "") })),
    journey: (payload.journey || []).map((event) => ({ eventType: String(event.event_type), occurredAt: String(event.occurred_at || ""), data: String(event.data || "{}") })),
  };
}

export function sendConversationMessage(id: string, text: string) {
  return readJson<{ status: string; messageId: string }>(`/api/admin/v1/conversations/${encodeURIComponent(id)}/messages`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
  });
}
